import { Screen } from 'src/lib/flows/screens';
import type { FlowStrengths, Progress } from 'src/lib/flows/flow-definition';
import { PrimaryGoal } from 'src/types/inputs';
import { Input, Strengths } from './constants';
import { InputValue } from 'src/redux/reducers/inputs';

export const getProgressValues = (
  progress: Partial<Progress> | undefined,
  screenName: Screen,
  primaryGoal: PrimaryGoal | undefined
): Progress => {
  if (screenName === Screen.AnnualFees) {
    switch (primaryGoal) {
      case PrimaryGoal.Debt:
      case PrimaryGoal.BigPurchase:
        return {
          show: true,
          label: 'Annual fees',
          value: 2,
          max: 2,
        };
      default:
        return {
          show: progress?.show ?? true,
          label: progress?.label ?? 'Annual fees',
          value: progress?.value ?? 3,
          max: progress?.max ?? 3,
        };
    }
  } else if (screenName === Screen.HowLongToPayOffDebt) {
    switch (primaryGoal) {
      case PrimaryGoal.Debt:
        return {
          show: true,
          label: 'Debt information',
          value: 1,
          max: 2,
        };
      default:
        return {
          show: true,
          label: 'Payment plans',
          value: 1,
          max: 2,
        };
    }
  }

  return {
    show: progress?.show ?? false,
    label: progress?.label ?? '',
    value: progress?.value ?? 1,
    max: progress?.max ?? 1,
  };
};

// function to determine the actual label to be displayed per strength
export const getStrengthLabel = (strength: Strengths | undefined): string => {
  switch (strength) {
    case Strengths.Weak:
    case Strengths.WeakPlus:
      return 'Weak';
    case Strengths.Average:
    case Strengths.AveragePlus:
      return 'Average';
    case Strengths.Intermediate:
    case Strengths.IntermediatePlus:
      return 'Intermediate';
    case Strengths.Strong:
    case Strengths.StrongPlus:
      return 'Strong';
    case Strengths.Strongest:
      return 'Strongest';
    default:
      return '';
  }
};

// function to determine the slider section of rec strengths
export const getStrengthValues = (strength: Strengths | undefined): number => {
  switch (strength) {
    case Strengths.Weak:
      return 18;
    case Strengths.WeakPlus:
      return 28;
    case Strengths.Average:
      return 38;
    case Strengths.AveragePlus:
      return 48;
    case Strengths.Intermediate:
      return 58;
    case Strengths.IntermediatePlus:
      return 68;
    case Strengths.Strong:
      return 78;
    case Strengths.StrongPlus:
      return 88;
    case Strengths.Strongest:
      return 100;
    default:
      return 8;
  }
};

// function to handle edge cases for shared questions in which the
// strengths would be different depending on the screen/primaryGoal
export const getRecStrength = (
  strengths: Partial<FlowStrengths> | undefined,
  screenName: Screen,
  inputs: Partial<Record<Input, InputValue>>
): FlowStrengths => {
  const primaryGoal = inputs.primaryGoal as PrimaryGoal;

  if (screenName === Screen.AnnualFees) {
    switch (primaryGoal) {
      case PrimaryGoal.BuildCredit:
      case PrimaryGoal.BigPurchase:
        return {
          start: Strengths.Strong,
          end: Strengths.Strongest,
        };
      default:
        return {
          start: Strengths.StrongPlus,
          end: Strengths.Strongest,
        };
    }
  } else if (screenName === Screen.HowLongToPayOffDebt) {
    switch (primaryGoal) {
      case PrimaryGoal.Debt:
        return {
          start: Strengths.Intermediate,
          end: Strengths.IntermediatePlus,
        };
      default:
        return {
          start: Strengths.AveragePlus,
          end: Strengths.Strong,
        };
    }
  }

  return {
    start: strengths?.start || Strengths.Weak,
    end: strengths?.end || Strengths.Weak,
  };
};

export const getExitModalRedirectLink = (
  primaryGoal: PrimaryGoal | undefined,
  isStudent: boolean
) => {
  let redirectLink = '';
  switch (primaryGoal) {
    case PrimaryGoal.Travel:
      redirectLink = '/best/credit-cards/travel';
      break;
    case PrimaryGoal.CashBack:
      redirectLink = '/best/credit-cards/cash-back';
      break;
    case PrimaryGoal.Debt:
      redirectLink = '/best/credit-cards/balance-transfer';
      break;
    case PrimaryGoal.BigPurchase:
      redirectLink = '/best/credit-cards/low-interest';
      break;
    case PrimaryGoal.BuildCredit:
      redirectLink = isStudent
        ? '/best/credit-cards/college-student'
        : '/best/credit-cards/bad-credit';
      break;
    default:
      redirectLink = '/the-best-credit-cards';
      break;
  }
  return redirectLink;
};
